/**
 * PropSelectionCard.jsx
 * 
 * Phase 2F5 - Extracted from GMRosterBuilderV2
 * Displays prop selection with over/below buttons and unit allocation.
 * 
 * BSL Compliant: Neutral colors, no red/green judgment.
 * Props only - no edge computation, displays server-provided values.
 */

import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { LabeledEdgeBar } from '../viz';

/**
 * @param {Object} props
 * @param {Object} props.prop - Prop data from player
 * @param {boolean} props.isSelected - Whether this prop is selected
 * @param {string} props.selectedSide - 'ABOVE' or 'BELOW' if selected
 * @param {number} props.units - Current unit allocation
 * @param {number} props.maxUnits - Maximum units available
 * @param {function} props.onSelect - Callback when prop/side is selected
 * @param {function} props.onUnitsChange - Callback when units change
 */
const PropSelectionCard = ({ prop, isSelected, selectedSide, units, maxUnits, onSelect, onUnitsChange }) => {
  // Display server-provided edge value (no computation)
  const edgeValue = prop.edgeValue ?? 0;
  
  return (
    <div className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}>
      <div className="p-3">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h4 className="text-base font-bold text-slate-800">{prop.stat}</h4>
            <p className="text-xs text-slate-500">
              Avg: <span className="font-bold tabular-nums">{prop.playerSeasonAvg?.toFixed(1) || '--'}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black text-slate-900 tabular-nums">{prop.line}</p>
            <p className="text-[10px] text-slate-400">Line</p>
          </div>
        </div>
        
        {/* Edge visualization - displays server value only */}
        <div className="bg-slate-50 rounded-lg p-2 mb-3">
          <LabeledEdgeBar 
            value={edgeValue} 
            label="Edge" 
            maxEdge={30} 
          />
          <div className="flex justify-between mt-1">
            <span className="text-[9px] text-slate-400">← Below</span>
            <span className="text-[9px] text-slate-400">Above →</span>
          </div>
        </div>
        
        {/* ABOVE/BELOW buttons */}
        <div className="flex gap-2">
          <button 
            onClick={() => onSelect(prop, 'ABOVE')}
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all active:scale-[0.98]
              ${isSelected && selectedSide === 'ABOVE' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
          >
            ABOVE
          </button>
          <button 
            onClick={() => onSelect(prop, 'BELOW')}
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all active:scale-[0.98]
              ${isSelected && selectedSide === 'BELOW' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-700'}`}
          >
            BELOW
          </button>
        </div>
        
        {/* Dollar allocation (only when selected) */}
        {isSelected && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs font-semibold text-slate-500">ALLOCATION</span>
                <p className={`text-sm font-medium mt-0.5 ${maxUnits - units <= 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                  ${maxUnits} max for this strategy
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => onUnitsChange(Math.max(10, units - 5))} 
                  disabled={units <= 10}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:scale-90 disabled:opacity-40"
                >
                  <Minus size={14} />
                </button>
                <span className="text-xl font-black text-indigo-600 w-14 text-center tabular-nums">${units}</span>
                <button 
                  onClick={() => onUnitsChange(Math.min(maxUnits, units + 5))} 
                  disabled={units >= maxUnits}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:scale-90 disabled:opacity-40"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            {units >= maxUnits && (
              <p className="text-[10px] text-amber-600 text-right mt-1">Maximum allocation reached</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropSelectionCard;
